/**
 * Track Metadata Cache Store
 *
 * Caches track metadata (name, artist, album, images) for tracks
 * discovered from playlist searches. This allows RecsPanel to display
 * track info even when tracks are not in the workbench.
 */

import { create } from 'zustand';
import type { Track, Album, Artist } from '../api/types';

export interface TrackMetadata {
  id: string;
  name: string;
  artists: Artist[];
  album: Album | null;
  uri: string;
}

interface TrackMetadataState {
  // Map of track ID -> metadata
  metadata: Map<string, TrackMetadata>;

  // Add single track metadata
  addTrack: (track: Track) => void;

  // Add multiple tracks at once
  addTracks: (tracks: Track[]) => void;

  // Get track metadata by ID
  getTrack: (trackId: string) => TrackMetadata | undefined;

  // Clear cache (for memory management)
  clear: () => void;
}

export const useTrackMetadataStore = create<TrackMetadataState>((set, get) => ({
  metadata: new Map(),

  addTrack: (track: Track) => {
    set((state) => {
      const newMetadata = new Map(state.metadata);
      newMetadata.set(track.id, {
        id: track.id,
        name: track.name,
        artists: track.artists,
        album: track.album,
        uri: track.uri,
      });
      return { metadata: newMetadata };
    });
  },

  addTracks: (tracks: Track[]) => {
    set((state) => {
      const newMetadata = new Map(state.metadata);
      for (const track of tracks) {
        newMetadata.set(track.id, {
          id: track.id,
          name: track.name,
          artists: track.artists,
          album: track.album,
          uri: track.uri,
        });
      }
      return { metadata: newMetadata };
    });
  },

  getTrack: (trackId: string) => {
    return get().metadata.get(trackId);
  },

  clear: () => {
    set({ metadata: new Map() });
  },
}));
